import type { IssueJobIds, IssueQueue } from "@domain/CRM/job";
import { visibleJobErrorQueues } from "@gui/pages/Admin/JobErrors/jobErrorTabs";

/**
 * The issue-count badges, as pure rules: which issues queues each badge counts for a viewer, and how
 * the job ids on those queues become the badge's number. `useIssueCountsState` asks the API for the
 * queues in one request and hands the answer back here.
 *
 * Kept free of the Toolbar barrel on purpose (the caller passes the visible paths in): the sidebar
 * lives in that barrel and reads `ISSUE_COUNT_PATHS`, so importing it here would close an import cycle.
 */

/** What one badge counts: its queues for this viewer, and how those queues' job ids become one number. */
type BadgeSource = {
	queues: (isDeveloper: boolean) => readonly IssueQueue[];
	count: (idsByQueue: readonly (readonly string[])[]) => number;
};

/** A page that lists a single queue. Its badge is that list's length — the number the page pushes itself. */
const oneQueue = (queue: IssueQueue): BadgeSource => ({ queues: () => [queue], count: ([ids]) => ids.length });

/**
 * Maps each path that has an "issues" count badge to what the badge counts. Source of truth for which
 * routes have a badge. Each entry must count exactly what its page lists for this user, or the badge
 * and the page disagree.
 */
const BADGES: Record<string, BadgeSource> = {
	"/admin/deal-matcher": oneQueue("deal-matcher"),
	"/admin/branch-matcher": oneQueue("branch-matcher"),
	"/admin/agrian-issues": oneQueue("agrian"),
	// One queue per tab the viewer may open; a job can sit on more than one tab and is counted once.
	"/admin/job-errors": { queues: visibleJobErrorQueues, count: (idsByQueue) => new Set(idsByQueue.flat()).size },
	"/admin/skips": oneQueue("skips"),
};

/** Set of paths that have a count badge — used by sidebar / Home to know which links should render a
 *  badge slot at all. */
export const ISSUE_COUNT_PATHS: ReadonlySet<string> = new Set(Object.keys(BADGES));

/** The queues behind each badge a viewer can see, keyed by the badge's route path. */
export type IssueQueuesByPath = Record<string, readonly IssueQueue[]>;

/** The queues behind every badge whose path this viewer can see. */
export function issueQueuesByPath(visiblePaths: ReadonlySet<string>, isDeveloper: boolean): IssueQueuesByPath {
	const byPath: Record<string, readonly IssueQueue[]> = {};
	for (const [path, badge] of Object.entries(BADGES)) {
		if (visiblePaths.has(path)) byPath[path] = badge.queues(isDeveloper);
	}
	return byPath;
}

/** Every queue those badges need, each once — the list to ask the API for. */
export function issueQueuesToRequest(queuesByPath: IssueQueuesByPath): IssueQueue[] {
	return [...new Set(Object.values(queuesByPath).flat())];
}

/**
 * The count for each badge, from the job ids the API answered per queue. A badge has no entry — its
 * count stays unknown, never 0 — when any queue it counts was not answered: one failed queue costs
 * exactly the badges that count it, and the Job Errors badge needs every tab the viewer can open.
 */
export function issueCountsFromIds(queuesByPath: IssueQueuesByPath, answer: IssueJobIds): Record<string, number> {
	const counts: Record<string, number> = {};
	for (const [path, queues] of Object.entries(queuesByPath)) {
		const idsByQueue = queues.map((queue) => (answer.failed.includes(queue) ? undefined : answer.ids[queue]));
		if (idsByQueue.every((ids): ids is string[] => ids !== undefined)) counts[path] = BADGES[path].count(idsByQueue);
	}
	return counts;
}
